<template>
  <div>
    <v-layout row justify-center>
      <v-dialog
        v-model="show"
        fullscreen
        hide-overlay
        persistent
        transition="dialog-bottom-transition"
      >
        <v-card class="invoice-viewer">
          <v-toolbar dark color="primary">
            <v-btn icon dark @click="onClickCloseModal()">
              <v-icon>close</v-icon>
            </v-btn>
            <v-toolbar-title>{{ toolbarTitle }}</v-toolbar-title>
            <v-spacer></v-spacer>
          </v-toolbar>

          <v-progress-linear v-if="loading" indeterminate color="primary"></v-progress-linear>

          <v-container fluid grid-list-lg class="invoice-viewer__content">
            <v-layout row wrap>
              <v-flex xs12 md4>
                <v-card flat class="invoice-viewer__panel">
                  <v-card-title>
                    <h2 class="mb-0">{{ invoiceTitle }}</h2>
                  </v-card-title>
                  <v-card-text v-if="invoiceData">
                    <section class="invoice-viewer__section">
                      <h3>Datos de la factura</h3>
                      <dl class="invoice-viewer__details">
                        <dt>Número</dt>
                        <dd>{{ invoiceData.code || '-' }}</dd>
                        <dt>Fecha de emisión</dt>
                        <dd>{{ formatDate(invoiceData.date) }}</dd>
                        <dt>Fecha de vencimiento</dt>
                        <dd>{{ formatDate(invoiceData.expiration_date) }}</dd>
                        <dt>Estado</dt>
                        <dd>{{ invoiceData.status || '-' }}</dd>
                        <dt>Tipo de documento</dt>
                        <dd>{{ documentTypeLabel }}</dd>
                        <dt>Empresa</dt>
                        <dd>{{ invoiceData.clavedat || '-' }}</dd>
                      </dl>
                    </section>

                    <section class="invoice-viewer__section">
                      <h3>Cliente</h3>
                      <dl class="invoice-viewer__details">
                        <dt>Nombre</dt>
                        <dd>{{ invoiceData.contact_name || '-' }}</dd>
                        <dt>NIF / CIF</dt>
                        <dd>{{ invoiceData.contact_cif || '-' }}</dd>
                        <dt>Dirección</dt>
                        <dd>{{ contactAddress }}</dd>
                        <dt>Email</dt>
                        <dd>{{ invoiceData.contact_email || '-' }}</dd>
                        <dt>Teléfono</dt>
                        <dd>{{ invoiceData.contact_phone || '-' }}</dd>
                      </dl>
                    </section>

                    <section class="invoice-viewer__section">
                      <h3>Importes</h3>
                      <dl class="invoice-viewer__details">
                        <dt>Base imponible</dt>
                        <dd>{{ formatMoney(invoiceData.tax_base) }}</dd>
                        <template v-if="taxBreakdownRows.length">
                          <template v-for="taxRow in taxBreakdownRows">
                            <dt :key="`${taxRow.key}-base-label`">Base {{ taxRow.label }}</dt>
                            <dd :key="`${taxRow.key}-base-value`">
                              {{ formatMoney(taxRow.base) }}
                            </dd>
                            <dt :key="`${taxRow.key}-amount-label`">
                              {{ taxRow.label }}
                            </dt>
                            <dd :key="`${taxRow.key}-amount-value`">
                              {{ formatMoney(taxRow.amount) }}
                            </dd>
                          </template>
                        </template>
                        <template v-if="irpfBreakdownRows.length">
                          <template v-for="irpfRow in irpfBreakdownRows">
                            <dt :key="`${irpfRow.key}-label`">
                              {{ irpfRow.label }}
                            </dt>
                            <dd :key="`${irpfRow.key}-value`">
                              {{ formatMoney(irpfRow.amount) }}
                            </dd>
                          </template>
                        </template>
                        <template v-if="totalSuplidos">
                          <dt>Suplidos</dt>
                          <dd>{{ formatMoney(totalSuplidos) }}</dd>
                        </template>
                        <dt>Total</dt>
                        <dd class="invoice-viewer__total">
                          {{ formatMoney(invoiceData.total_amount) }}
                        </dd>
                      </dl>
                    </section>

                    <section v-if="invoiceLines.length" class="invoice-viewer__section">
                      <h3>Líneas</h3>
                      <v-data-table
                        :headers="lineHeaders"
                        :items="invoiceLines"
                        hide-actions
                        class="invoice-viewer__lines"
                      >
                        <template v-slot:items="props">
                          <td>
                            <span
                              class="invoice-viewer__line-description"
                              :title="formatLineDescription(props.item.description)"
                            >
                              {{ formatLineDescription(props.item.description) }}
                            </span>
                          </td>
                          <td class="text-xs-right">{{ props.item.quantity || 0 }}</td>
                          <td class="text-xs-right">{{ formatMoney(getLineBase(props.item)) }}</td>
                          <td>{{ formatTaxLabel(props.item.tax) }}</td>
                          <td class="text-xs-right">
                            {{ formatMoney(getLineTaxAmount(props.item)) }}
                          </td>
                          <td class="text-xs-right">{{ formatMoney(props.item.total_amount) }}</td>
                          <td class="text-xs-right">
                            {{ formatMoney(getLineTotalWithTaxes(props.item)) }}
                          </td>
                        </template>
                      </v-data-table>
                    </section>

                    <section v-if="invoiceData.observations" class="invoice-viewer__section">
                      <h3>Observaciones</h3>
                      <p class="invoice-viewer__observations">
                        {{ invoiceData.observations }}
                      </p>
                    </section>
                  </v-card-text>

                  <v-card-text v-else-if="!loading">
                    No se ha podido cargar la factura.
                  </v-card-text>
                </v-card>
              </v-flex>

              <v-flex xs12 md8>
                <v-card flat class="invoice-viewer__preview">
                  <v-card-title>
                    <h2 class="mb-0">Vista previa PDF</h2>
                  </v-card-title>
                  <v-card-text>
                    <v-alert v-if="previewError" :value="true" color="error" outline>
                      {{ previewError }}
                    </v-alert>
                    <iframe
                      v-if="previewPdfUrl"
                      :key="previewPdfKey"
                      :src="previewPdfUrl"
                      class="invoice-viewer__iframe"
                      frameborder="0"
                    ></iframe>
                    <v-layout
                      v-else
                      align-center
                      justify-center
                      class="invoice-viewer__empty-preview"
                    >
                      <v-progress-circular
                        v-if="loadingPreview"
                        indeterminate
                        color="primary"
                      ></v-progress-circular>
                    </v-layout>
                  </v-card-text>
                </v-card>
              </v-flex>
            </v-layout>
          </v-container>
        </v-card>
      </v-dialog>

      <v-snackbar v-model="snack" :timeout="3000" :color="snackColor">
        {{ snackText }}
      </v-snackbar>
    </v-layout>
  </div>
</template>

<script>
import apiCtrl from '@/services/api/abstract/abstract-api';
import { OG_CONFIG } from '@/services/api/config.js';
import moment from 'moment';

export default {
  name: 'OpenBillingInvoiceForm',
  props: {
    showModal: { type: Boolean, default: false },
    invoiceId: { type: [String, Number], default: null },
    invoice: { type: Object, default: null },
    invoiceType: { type: String, default: 'factura' },
  },
  data() {
    return {
      invoiceData: null,
      loading: false,
      loadingPreview: false,
      previewPdfUrl: '',
      previewPdfKey: 0,
      previewObjectUrl: null,
      previewError: '',
      snack: false,
      snackText: '',
      snackColor: 'info',
      lineHeaders: [
        { text: 'Concepto', value: 'description', sortable: false },
        { text: 'Cantidad', value: 'quantity', sortable: false, align: 'right' },
        { text: 'Base', value: 'tax_base', sortable: false, align: 'right' },
        { text: 'Impuesto', value: 'tax', sortable: false },
        { text: 'Imp.', value: 'tax_amount', sortable: false, align: 'right' },
        { text: 'Subtotal', value: 'total_amount', sortable: false, align: 'right' },
        { text: 'Total', value: 'total_amount_with_taxes', sortable: false, align: 'right' },
      ],
    };
  },
  computed: {
    show() {
      return this.showModal;
    },
    toolbarTitle() {
      return this.invoiceType === 'presupuesto' ? 'VER PRESUPUESTO' : 'VER FACTURA';
    },
    invoiceTitle() {
      const code = this.invoiceData && this.invoiceData.code ? ` ${this.invoiceData.code}` : '';
      return `${this.documentTypeLabel}${code}`;
    },
    invoiceLines() {
      return this.invoiceData && this.invoiceData.order_items ? this.invoiceData.order_items : [];
    },
    documentTypeLabel() {
      const docType =
        this.invoiceData && this.invoiceData.doc_type
          ? this.invoiceData.doc_type
          : this.invoiceType;
      switch (docType) {
        case 'presupuesto':
          return 'Presupuesto';
        case 'ticket':
          return 'Ticket';
        default:
          return 'Factura';
      }
    },
    contactAddress() {
      if (!this.invoiceData) {
        return '-';
      }
      const parts = [
        this.invoiceData.contact_address,
        this.invoiceData.contact_postal_code,
        this.invoiceData.contact_city,
        this.invoiceData.contact_province,
        this.invoiceData.contact_country,
      ].filter(Boolean);
      return parts.length ? parts.join(', ') : '-';
    },
    taxBreakdownRows() {
      const rows = {};
      this.invoiceLines
        .filter(item => item.type !== 'suplido')
        .forEach(item => {
          const key = this.normalizeTaxKey(item.tax);
          const base = this.toAmount(this.getLineBase(item));
          const amount = this.toAmount(this.getLineTaxAmount(item));

          if (!rows[key]) {
            rows[key] = {
              key,
              label: this.formatTaxLabel(item.tax),
              base: 0,
              amount: 0,
            };
          }

          rows[key].base += base;
          rows[key].amount += amount;
        });

      return Object.values(rows).filter(
        row => this.hasAmount(row.base) || this.hasAmount(row.amount),
      );
    },
    irpfBreakdownRows() {
      const rows = {};
      this.invoiceLines
        .filter(item => item.type !== 'suplido' && this.hasIrpf(item.irpf))
        .forEach(item => {
          const key = this.normalizeRate(item.irpf);
          const amount = this.toAmount(this.getLineIrpfAmount(item));

          if (!rows[key]) {
            rows[key] = {
              key,
              label: this.formatIrpfLabel(item.irpf),
              amount: 0,
            };
          }

          rows[key].amount += amount;
        });

      return Object.values(rows).filter(row => this.hasAmount(row.amount));
    },
    totalSuplidos() {
      return this.invoiceLines
        .filter(item => item.type === 'suplido')
        .reduce((total, item) => total + this.toAmount(item.total_amount), 0);
    },
  },
  async mounted() {
    await this.loadInvoice();
  },
  beforeDestroy() {
    this.revokePreviewObjectUrl();
  },
  methods: {
    async loadInvoice() {
      this.loading = true;
      this.invoiceData = this.invoice ? Object.assign({}, this.invoice) : null;

      try {
        const clavedat = this.getInvoiceClavedat(this.invoiceData);
        const response = await apiCtrl.get(
          `/openbilling/${clavedat}/invoices/show/${this.invoiceId}`,
        );
        this.invoiceData = response.data.data;
        await this.previewPdf();
      } catch (error) {
        console.error('Error cargando factura:', error);
        if (this.invoiceData) {
          await this.previewPdf();
        } else {
          this.showSnackMessage('error', 'Se ha producido un error al cargar la factura');
        }
      }

      this.loading = false;
    },
    async previewPdf() {
      if (!this.invoiceData) {
        return;
      }

      this.loadingPreview = true;
      this.previewError = '';
      this.previewPdfUrl = '';
      this.revokePreviewObjectUrl();

      try {
        const clavedat = this.getInvoiceClavedat(this.invoiceData);
        const response = await apiCtrl.post(
          `/openbilling/${clavedat}/invoices/preview-pdf-token`,
          this.invoiceData,
        );
        const token = response.data && response.data.data ? response.data.data : response.data;
        this.previewPdfUrl = `${this.getApiRootUrl()}/invoices/preview-pdf/${token}`;
        this.previewPdfKey++;
      } catch (error) {
        console.error('Error generando token de preview PDF:', error);
        await this.previewPdfFromBlob();
      }

      this.loadingPreview = false;
    },
    async previewPdfFromBlob() {
      try {
        const clavedat = this.getInvoiceClavedat(this.invoiceData);
        const response = await apiCtrl.get(
          `/openbilling/${clavedat}/invoices/${this.invoiceData.id}/preview-pdf`,
          { responseType: 'blob' },
        );
        this.previewObjectUrl = window.URL.createObjectURL(
          new Blob([response.data], { type: 'application/pdf' }),
        );
        this.previewPdfUrl = this.previewObjectUrl;
        this.previewPdfKey++;
      } catch (error) {
        console.error('Error cargando preview PDF:', error);
        this.previewError = 'No se ha podido cargar la previsualización del PDF';
      }
    },
    getInvoiceClavedat(invoice) {
      return invoice && invoice.clavedat ? invoice.clavedat : 'null';
    },
    formatLineDescription(description) {
      if (!description) {
        return '-';
      }

      return String(description)
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    },
    getLineBase(item) {
      if (this.isNotEmpty(item.total_amount)) {
        return item.total_amount;
      }

      return item.tax_base;
    },
    getLineTaxAmount(item) {
      if (this.isNotEmpty(item.tax_amount)) {
        return item.tax_amount;
      }

      if (!this.isPercentageTax(item.tax)) {
        return 0;
      }

      return (this.toAmount(this.getLineBase(item)) * this.toAmount(item.tax)) / 100;
    },
    getLineIrpfAmount(item) {
      if (this.isNotEmpty(item.irpf_amount)) {
        return item.irpf_amount;
      }

      if (!this.hasIrpf(item.irpf)) {
        return 0;
      }

      return (-this.toAmount(this.getLineBase(item)) * this.toAmount(item.irpf)) / 100;
    },
    getLineTotalWithTaxes(item) {
      if (this.isNotEmpty(item.total_amount_with_taxes)) {
        return item.total_amount_with_taxes;
      }

      return (
        this.toAmount(this.getLineBase(item)) +
        this.toAmount(this.getLineTaxAmount(item)) +
        this.toAmount(this.getLineIrpfAmount(item))
      );
    },
    formatTaxLabel(tax) {
      const taxKey = this.normalizeTaxKey(tax);
      const labels = {
        21: 'IVA 21%',
        12: 'IVA 12%',
        '10.5': 'IVA 10,5%',
        10: 'IVA 10%',
        '7.5': 'IVA 7,5%',
        5: 'IVA 5%',
        4: 'IVA 4%',
        2: 'IVA 2%',
        0: 'IVA/IGIC 0%',
        iva_0: 'IVA 0%',
        iva_exenta: 'IVA exenta',
        iva_no_sujeta: 'IVA no sujeta',
        iva_intra_bienes: 'IVA intracom. bienes',
        iva_intra_servicios: 'IVA intracom. servicios',
        iva_exportacion: 'Exportacion IVA',
        iva_inversion_sujeta_pasivo: 'Inv. sujeto pasivo IVA',
        35: 'IGIC 35%',
        20: 'IGIC 20%',
        15: 'IGIC 15%',
        '9.5': 'IGIC 9,5%',
        7: 'IGIC 7%',
        3: 'IGIC 3%',
        '1.5': 'IGIC 1,5%',
        igic_0: 'IGIC 0%',
        igic_exenta: 'IGIC exenta',
        igic_no_sujeta: 'IGIC no sujeta',
        igic_exportacion: 'Exportacion IGIC',
        igic_inversion_sujeta_pasivo: 'Inv. sujeto pasivo IGIC',
        exento: 'Exento IVA/IGIC',
      };

      return labels[taxKey] || `${tax}%`;
    },
    formatIrpfLabel(irpf) {
      return this.hasIrpf(irpf) ? `IRPF ${this.normalizeRate(irpf)}%` : '-';
    },
    normalizeTaxKey(tax) {
      if (tax === null || tax === undefined || tax === '') {
        return 'exento';
      }

      return String(tax);
    },
    normalizeRate(rate) {
      return String(rate).replace(',', '.');
    },
    isPercentageTax(tax) {
      return this.isNotEmpty(tax) && !Number.isNaN(this.toNumber(tax));
    },
    hasIrpf(irpf) {
      return this.isNotEmpty(irpf) && this.toAmount(irpf) !== 0;
    },
    isNotEmpty(value) {
      return value !== null && value !== undefined && value !== '';
    },
    hasAmount(value) {
      return Math.abs(this.toAmount(value)) > 0.000001;
    },
    toAmount(value) {
      if (!this.isNotEmpty(value)) {
        return 0;
      }

      const amount = this.toNumber(value);
      return Number.isNaN(amount) ? 0 : amount;
    },
    toNumber(value) {
      const normalizedValue = typeof value === 'string' ? value.replace(',', '.') : value;
      return Number(normalizedValue);
    },
    getApiRootUrl() {
      return OG_CONFIG.BASE_URL || apiCtrl.getBaseUrl().replace(/\/api\/v1\/?$/, '');
    },
    revokePreviewObjectUrl() {
      if (this.previewObjectUrl) {
        window.URL.revokeObjectURL(this.previewObjectUrl);
        this.previewObjectUrl = null;
      }
    },
    formatDate(date) {
      return date ? moment(date).format('DD/MM/YYYY') : '-';
    },
    formatMoney(value) {
      if (value === null || value === undefined || value === '') {
        return '-';
      }
      const amount = this.toAmount(value);
      return `${amount.toFixed(2).replace('.', ',')} €`;
    },
    showSnackMessage(action, message) {
      this.snackColor = action;
      this.snackText = message;
      this.snack = true;
    },
    onClickCloseModal() {
      this.$emit('click-on-close');
    },
  },
};
</script>

<style scoped>
.invoice-viewer {
  background-color: #f5f5f5;
  min-height: 100vh;
}

.invoice-viewer__content {
  max-width: 1600px;
}

.invoice-viewer__panel,
.invoice-viewer__preview {
  background-color: #ffffff;
  min-height: calc(100vh - 112px);
}

.invoice-viewer__section {
  border-top: 1px solid #e5e5e5;
  padding: 18px 0;
}

.invoice-viewer__section:first-child {
  border-top: 0;
  padding-top: 0;
}

.invoice-viewer__section h3 {
  font-size: 1rem;
  margin-bottom: 12px;
}

.invoice-viewer__details {
  display: grid;
  grid-template-columns: minmax(120px, 42%) 1fr;
  grid-row-gap: 8px;
  margin: 0;
}

.invoice-viewer__details dt {
  color: #666666;
  font-weight: 600;
}

.invoice-viewer__details dd {
  margin: 0;
  text-align: right;
  word-break: break-word;
}

.invoice-viewer__total {
  font-size: 1.2rem;
  font-weight: 700;
}

.invoice-viewer__lines {
  border: 1px solid #eeeeee;
  overflow-x: auto;
}

.invoice-viewer__lines /deep/ table {
  min-width: 780px;
}

.invoice-viewer__line-description {
  display: inline-block;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
  white-space: nowrap;
}

.invoice-viewer__observations {
  margin: 0;
  white-space: pre-wrap;
}

.invoice-viewer__iframe {
  background-color: #ffffff;
  border: 1px solid #dddddd;
  min-height: 1200px;
  width: 100%;
}

.invoice-viewer__empty-preview {
  min-height: 400px;
}
</style>
